/**
 * The sentence/label pairs behind one policy version. Ported from the #5 prototype.
 *
 * Rendered **inline** under the rail rather than in a modal or a docked panel — all three
 * were built and compared in the prototype, and inline was Gareth's call (#5, question 4).
 *
 * With a full-text search over the labels, because the lists are long: one version carries
 * 576 labels, and only the first 100 are rendered, so without search the tail was
 * unreachable. Search filters before that cap, so a match at position 400 is findable.
 */
import { useMemo, useState } from 'react';
import { Box, Chip, Divider, InputAdornment, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { PolicyVersionLabel, PolicyVersionState } from '../../../types';
import { highlightSegments, HighlightedText } from '../../common/highlightText';

export { highlightSegments };

const MAX_SHOWN = 100;
const Highlighted = HighlightedText;

const typeColour = (type: string): 'success' | 'error' | 'default' =>
  type.toLowerCase().includes('pos')
    ? 'success'
    : type.toLowerCase().includes('neg')
      ? 'error'
      : 'default';

/**
 * Does this label match the search text?
 *
 * Searches the sentence, the labeller's reason, and the label type — all three are things
 * an admin might be hunting ("find where someone justified this with 'oxymoron'", "show me
 * the negatives"). Case-insensitive substring; no tokenising, because these are short
 * fragments and a substring is more predictable than word matching on partial sentences.
 */
export const matchesLabelQuery = (label: PolicyVersionLabel, query: string): boolean => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return [label.essayContentFragment, label.reason, label.type].some(field =>
    (field ?? '').toLowerCase().includes(needle)
  );
};

interface Props {
  version: PolicyVersionState;
  labels: PolicyVersionLabel[];
}

export const LabelSamplesList = ({ version, labels }: Props) => {
  const [query, setQuery] = useState('');

  const matching = useMemo(
    () => labels.filter(label => matchesLabelQuery(label, query)),
    [labels, query]
  );

  if (labels.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No labels attached to v{version.version}. Nothing at stake for this version.
      </Typography>
    );
  }

  const shown = matching.slice(0, MAX_SHOWN);
  const searching = query.trim().length > 0;

  return (
    <Box>
      {/* Sticky, because the panel scrolls: the field has to stay reachable while reading
          down a list of hundreds. */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: 'grey.50',
          pb: 1,
        }}
      >
        <TextField
          size="small"
          fullWidth
          placeholder={`Search ${version.labelCount.toLocaleString()} labels on v${version.version} — sentence, reason or type…`}
          value={query}
          onChange={event => setQuery(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {searching
            ? `${matching.length.toLocaleString()} of ${labels.length.toLocaleString()} labels match` +
              (matching.length > MAX_SHOWN ? `, showing the first ${MAX_SHOWN}` : '')
            : `Showing ${shown.length} of ${version.labelCount.toLocaleString()} labels on v${version.version}`}
        </Typography>
      </Box>

      {searching && matching.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No labels on v{version.version} match “{query.trim()}”.
        </Typography>
      ) : null}

      {shown.map(sample => (
        <Box key={sample.id} sx={{ py: 1.25 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Chip
              size="small"
              label={sample.type}
              color={typeColour(sample.type)}
              sx={{ flexShrink: 0 }}
            />
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              {sample.essayContentFragment?.trim() ? (
                <Highlighted text={sample.essayContentFragment.trim()} query={query} />
              ) : (
                <em>(whole essay — no fragment)</em>
              )}
            </Typography>
          </Box>
          {sample.reason ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5, pl: 1 }}
            >
              <Highlighted text={sample.reason} query={query} />
            </Typography>
          ) : null}
          <Divider sx={{ mt: 1 }} />
        </Box>
      ))}
    </Box>
  );
};
